import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

interface Scanner {
  id: string
  device_id: string
  name: string
  manufacturer: string
  model: string
  serial_number: string
  location: string
  department: string
  status: 'online' | 'offline' | 'scanning' | 'maintenance' | 'error'
  resolution: string
  scan_speed: number
  supported_formats: string[]
  current_job?: ScanJob
  total_scans: number
  hours_operated: number
  last_maintenance: string
  next_maintenance: string
  calibration_status: 'calibrated' | 'needs_calibration' | 'overdue'
  last_calibration: string
  ip_address: string
  firmware_version: string
  driver_version: string
}

interface ScanJob {
  id: string
  scanner_id: string
  patient_id: string
  patient_name: string
  case_id: string
  scan_type: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  status: 'queued' | 'scanning' | 'completed' | 'failed' | 'cancelled'
  started_at?: string
  completed_at?: string
  progress: number
  estimated_time_remaining?: number
  file_size?: number
  file_path?: string
  error_message?: string
}

interface ScannerSettings {
  resolution: string
  color_mode: 'color' | 'grayscale' | 'bw'
  compression: 'none' | 'low' | 'medium' | 'high'
  auto_crop: boolean
  auto_rotate: boolean
  format: 'tiff' | 'jpeg' | 'png' | 'pdf'
}

const ScannerManagement: React.FC = () => {
  const [scanners, setScanners] = useState<Scanner[]>([])
  const [jobs, setJobs] = useState<ScanJob[]>([])
  const [selectedScanner, setSelectedScanner] = useState<Scanner | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [locationFilter, setLocationFilter] = useState<string>('all')
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [, setShowJobModal] = useState(false)
  const [showCalibrationModal, setShowCalibrationModal] = useState(false)

  const [scannerSettings, setScannerSettings] = useState<ScannerSettings>({
    resolution: '600',
    color_mode: 'color',
    compression: 'medium',
    auto_crop: true,
    auto_rotate: true,
    format: 'tiff'
  })

  const fetchScanners = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/v1/devices/scanners/')
      if (response.ok) {
        const data = await response.json()
        setScanners(data.results || data)
      } else {
        toast.error('Failed to fetch scanners')
      }
    } catch (error) {
      console.error('Error fetching scanners:', error)
      toast.error('Error fetching scanners')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchJobs = async () => {
    try {
      const response = await fetch('/api/v1/devices/scan-jobs/')
      if (response.ok) {
        const data = await response.json()
        setJobs(data.results || data)
      }
    } catch (error) {
      console.error('Error fetching scan jobs:', error)
    }
  }

  useEffect(() => {
    fetchScanners()
    fetchJobs()

    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchScanners()
      fetchJobs()
    }, 10000)

    return () => clearInterval(interval)
  }, [])


  const cancelJob = async (jobId: string) => {
    try {
      const response = await fetch(`/api/v1/devices/scan-jobs/${jobId}/cancel/`, {
        method: 'POST'
      })

      if (response.ok) {
        toast.success('Scan job cancelled')
        fetchJobs()
      } else {
        toast.error('Failed to cancel scan job')
      }
    } catch (error) {
      console.error('Error cancelling job:', error)
      toast.error('Error cancelling job')
    }
  }

  const calibrateScanner = async (scannerId: string) => {
    try {
      const response = await fetch(`/api/v1/devices/scanners/${scannerId}/calibrate/`, {
        method: 'POST'
      })

      if (response.ok) {
        toast.success('Scanner calibration started')
        fetchScanners()
        setShowCalibrationModal(false)
      } else {
        toast.error('Failed to start calibration')
      }
    } catch (error) {
      console.error('Error calibrating scanner:', error)
      toast.error('Error calibrating scanner')
    }
  }

  const updateScannerSettings = async (scannerId: string, settings: ScannerSettings) => {
    try {
      const response = await fetch(`/api/v1/devices/scanners/${scannerId}/settings/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      })

      if (response.ok) {
        toast.success('Scanner settings updated')
        fetchScanners()
        setShowConfigModal(false)
      } else {
        toast.error('Failed to update settings')
      }
    } catch (error) {
      console.error('Error updating settings:', error)
      toast.error('Error updating settings')
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      online: 'bg-green-100 text-green-800',
      offline: 'bg-red-100 text-red-800',
      scanning: 'bg-blue-100 text-blue-800',
      maintenance: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-200 text-red-900'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      online: '🟢',
      offline: '🔴',
      scanning: '🔵',
      maintenance: '🟡',
      error: '❌'
    }
    return icons[status] || '⚫'
  }

  const getCalibrationColor = (status: string) => {
    const colors: Record<string, string> = {
      calibrated: 'bg-green-100 text-green-800',
      needs_calibration: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-800',
      normal: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    }
    return colors[priority] || 'bg-gray-100 text-gray-800'
  }

  const filteredScanners = scanners.filter(scanner => {
    const matchesSearch =
      scanner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scanner.device_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scanner.location.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || scanner.status === statusFilter
    const matchesLocation = locationFilter === 'all' || scanner.location === locationFilter

    return matchesSearch && matchesStatus && matchesLocation
  })

  const locations = [...new Set(scanners.map(s => s.location))]
  const activeJobs = jobs.filter(j => j.status === 'queued' || j.status === 'scanning')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scanner Management</h1>
          <p className="text-gray-600">Manage scanners, jobs, and calibration</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowJobModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            New Scan Job
          </button>
          <button className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">
            Bulk Calibration
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {[
          { label: 'Total Scanners', value: scanners.length, color: 'blue', icon: '🖨️' },
          { label: 'Online', value: scanners.filter(s => s.status === 'online').length, color: 'green', icon: '🟢' },
          { label: 'Scanning', value: scanners.filter(s => s.status === 'scanning').length, color: 'blue', icon: '🔵' },
          { label: 'Active Jobs', value: activeJobs.length, color: 'purple', icon: '📋' },
          { label: 'Need Calibration', value: scanners.filter(s => s.calibration_status !== 'calibrated').length, color: 'yellow', icon: '⚙️' }
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-6 rounded-lg border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className="text-2xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Scan Jobs</h3>
          <div className="space-y-3">
            {activeJobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(job.priority)}`}>
                      {job.priority.toUpperCase()}
                    </span>
                    <span className="font-medium">{job.patient_name}</span>
                    <span className="text-gray-500">({job.case_id})</span>
                  </div>
                  <p className="text-sm text-gray-600">{job.scan_type}</p>
                  {job.status === 'scanning' && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${job.progress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {job.progress}% complete
                        {job.estimated_time_remaining && ` • ${Math.round(job.estimated_time_remaining / 60)} min remaining`}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    job.status === 'scanning' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {job.status.toUpperCase()}
                  </span>
                  <button
                    onClick={() => cancelJob(job.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search scanners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="scanning">Scanning</option>
              <option value="maintenance">Maintenance</option>
              <option value="error">Error</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Locations</option>
              {locations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Scanners Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading scanners...</p>
          </div>
        ) : filteredScanners.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg border">
            <p className="text-gray-500">No scanners found</p>
          </div>
        ) : (
          filteredScanners.map((scanner) => (
            <div key={scanner.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
              {/* Scanner Header */}
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{scanner.name}</h3>
                  <p className="text-sm text-gray-600">{scanner.device_id}</p>
                  <p className="text-xs text-gray-500">{scanner.manufacturer} {scanner.model}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{getStatusIcon(scanner.status)}</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(scanner.status)}`}>
                    {scanner.status.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Scanner Info */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium text-gray-900">{scanner.location}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Resolution:</span>
                  <span className="font-medium text-gray-900">{scanner.resolution} DPI</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Scans:</span>
                  <span className="font-medium text-gray-900">{scanner.total_scans.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Calibration:</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${getCalibrationColor(scanner.calibration_status)}`}>
                    {scanner.calibration_status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Current Job */}
              {scanner.current_job && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">Current Job:</p>
                  <p className="text-sm text-blue-800">{scanner.current_job.patient_name}</p>
                  <div className="mt-1">
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${scanner.current_job.progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">{scanner.current_job.progress}% complete</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center">
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedScanner(scanner)
                      setShowConfigModal(true)
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Configure
                  </button>
                  <button
                    onClick={() => {
                      setSelectedScanner(scanner)
                      setShowCalibrationModal(true)
                    }}
                    className="text-green-600 hover:text-green-800 text-sm"
                  >
                    Calibrate
                  </button>
                </div>
                <button className="text-gray-600 hover:text-gray-800 text-sm">
                  Maintenance
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Configuration Modal */}
      {showConfigModal && selectedScanner && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Scanner Settings</h3>
              <button
                onClick={() => setShowConfigModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resolution (DPI)</label>
                <select
                  value={scannerSettings.resolution}
                  onChange={(e) => setScannerSettings({...scannerSettings, resolution: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="300">300 DPI</option>
                  <option value="600">600 DPI</option>
                  <option value="1200">1200 DPI</option>
                  <option value="2400">2400 DPI</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color Mode</label>
                <select
                  value={scannerSettings.color_mode}
                  onChange={(e) => setScannerSettings({...scannerSettings, color_mode: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="color">Color</option>
                  <option value="grayscale">Grayscale</option>
                  <option value="bw">Black & White</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">File Format</label>
                <select
                  value={scannerSettings.format}
                  onChange={(e) => setScannerSettings({...scannerSettings, format: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="tiff">TIFF</option>
                  <option value="jpeg">JPEG</option>
                  <option value="png">PNG</option>
                  <option value="pdf">PDF</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Compression</label>
                <select
                  value={scannerSettings.compression}
                  onChange={(e) => setScannerSettings({...scannerSettings, compression: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="none">None</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={scannerSettings.auto_crop}
                    onChange={(e) => setScannerSettings({...scannerSettings, auto_crop: e.target.checked})}
                    className="mr-2"
                  />
                  Auto Crop
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={scannerSettings.auto_rotate}
                    onChange={(e) => setScannerSettings({...scannerSettings, auto_rotate: e.target.checked})}
                    className="mr-2"
                  />
                  Auto Rotate
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => updateScannerSettings(selectedScanner.id, scannerSettings)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calibration Modal */}
      {showCalibrationModal && selectedScanner && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Scanner Calibration</h3>
              <button
                onClick={() => setShowCalibrationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="font-medium text-gray-900">{selectedScanner.name}</p>
                <p className="text-sm text-gray-600">{selectedScanner.device_id}</p>
              </div>

              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Current Status:</span>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getCalibrationColor(selectedScanner.calibration_status)}`}>
                    {selectedScanner.calibration_status.replace('_', ' ').toUpperCase()}
                  </span>
                </p>
                <p><span className="font-medium">Last Calibration:</span> {new Date(selectedScanner.last_calibration).toLocaleDateString()}</p>
              </div>

              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> Calibration will temporarily stop all scanning operations on this device.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowCalibrationModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => calibrateScanner(selectedScanner.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
              >
                Start Calibration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ScannerManagement